import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface Category {
  name: string;
  url: string;
  icon: string;
}

const categories: Category[] = [
  { name: 'Buty', url: 'https://kaspreadsheet.com/category-911-Shoes.html', icon: 'ri-footprint-line' },
  { name: 'Bluzy/Swetry', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html', icon: 'ri-t-shirt-line' },
  { name: 'Koszulki', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html', icon: 'ri-t-shirt-2-line' },
  { name: 'Kurtki', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html', icon: 'ri-honour-line' },
  { name: 'Spodnie/Szorty', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html', icon: 'ri-pantone-line' },
  { name: 'Nakrycia głowy', url: 'https://kaspreadsheet.com/search-916/name/Shoes.html', icon: 'ri-user-3-line' },
  { name: 'Zestawy', url: 'https://kaspreadsheet.com/search-917/name/Shoes.html', icon: 'ri-stack-line' },
  { name: 'Bielizna', url: 'https://kaspreadsheet.com/search-918/name/Shoes.html', icon: 'ri-circle-line' },
  { name: 'Koszulki sportowe', url: 'https://kaspreadsheet.com/search-919/name/Shoes.html', icon: 'ri-shirt-line' },
  { name: 'Akcesoria', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html', icon: 'ri-glasses-line' },
  { name: 'Torby', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html', icon: 'ri-handbag-line' },
  { name: 'Paski', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html', icon: 'ri-link' },
  { name: 'Perfumy', url: 'https://kaspreadsheet.com/search-923/name/Shoes.html', icon: 'ri-contrast-drop-line' },
  { name: 'Inne', url: 'https://kaspreadsheet.com/search-924/name/Shoes.html', icon: 'ri-more-line' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileCatExpanded, setMobileCatExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const nav = document.getElementById('main-nav');
      if (nav) {
        if (window.scrollY > 20) {
          nav.classList.add('bg-background-50/95', 'backdrop-blur-md');
          nav.classList.remove('bg-transparent');
        } else {
          nav.classList.remove('bg-background-50/95', 'backdrop-blur-md');
          nav.classList.add('bg-transparent');
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  return (
    <>
      <nav id="main-nav" className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-transparent">
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-18">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <span className="font-heading text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">
                Kakobuy<span className="text-primary-500">Finds</span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              {/* Category Dropdown */}
              <div ref={dropdownRef} className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 hover:bg-background-200 text-foreground-800 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-grid-line w-4 h-4 flex items-center justify-center"></i>
                  <span>{t('nav.categories')}</span>
                  <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isDropdownOpen && (
                  <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 rounded-xl border border-background-200/70 overflow-hidden z-50">
                    <div className="max-h-80 overflow-y-auto py-2">
                      {categories.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-foreground-400`}></i>
                          {cat.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Nav Links */}
              <Link to="/" className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap">
                {t('nav.home')}
              </Link>
              <Link to="/artykul/kakobuy-sheet-kompletny-przewodnik" className="text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap">
                {t('nav.articles')}
              </Link>

              {/* CTA Buttons */}
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('nav.kakobuySheet')}
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('nav.buyNow')}
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 cursor-pointer"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl text-foreground-800`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="absolute inset-0 bg-foreground-950/30 backdrop-blur-sm"></div>
        </div>
      )}

      {/* Mobile Menu Panel */}
      <div
        ref={mobileMenuRef}
        className={`md:hidden fixed top-0 right-0 z-50 h-full w-80 max-w-[85vw] bg-background-50 transform transition-transform duration-300 ease-in-out overflow-y-auto ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Mobile Menu Header */}
        <div className="sticky top-0 bg-background-50/95 backdrop-blur-md border-b border-background-200/70 px-5 py-4 flex items-center justify-between z-10">
          <span className="font-heading text-lg font-bold text-foreground-950">
            Menu
          </span>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center cursor-pointer"
          >
            <i className="ri-close-line text-foreground-700 w-4 h-4 flex items-center justify-center"></i>
          </button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* Quick CTA buttons */}
          <div className="flex gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex-1 px-4 py-2.5 rounded-full bg-foreground-950 text-background-50 text-sm font-semibold text-center hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('nav.kakobuySheet')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex-1 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold text-center hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('nav.buyNow')}
            </a>
          </div>

          {/* Navigation links */}
          <div className="space-y-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <i className="ri-home-4-line w-5 h-5 flex items-center justify-center text-foreground-400"></i>
              {t('nav.home')}
            </Link>
            <Link
              to="/artykul/kakobuy-sheet-kompletny-przewodnik"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <i className="ri-article-line w-5 h-5 flex items-center justify-center text-foreground-400"></i>
              {t('nav.articles')}
            </Link>
          </div>

          {/* Articles section */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-3 mb-2">
              Poradniki SEO
            </h4>
            <div className="space-y-1">
              <Link
                to="/artykul/kakobuy-qc-guide"
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="ri-search-eye-line w-5 h-5 flex items-center justify-center text-foreground-400"></i>
                <div>
                  <span className="block text-sm font-medium text-foreground-800">QC Guide</span>
                  <span className="block text-xs text-foreground-400">Jak sprawdzać jakość</span>
                </div>
              </Link>
              <Link
                to="/artykul/kakobuy-wysylka-do-polski"
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="ri-ship-line w-5 h-5 flex items-center justify-center text-foreground-400"></i>
                <div>
                  <span className="block text-sm font-medium text-foreground-800">Wysyłka do Polski</span>
                  <span className="block text-xs text-foreground-400">Metody i koszty</span>
                </div>
              </Link>
              <Link
                to="/artykul/kakobuy-porownanie-agentow"
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="ri-scales-3-line w-5 h-5 flex items-center justify-center text-foreground-400"></i>
                <div>
                  <span className="block text-sm font-medium text-foreground-800">Porównanie agentów</span>
                  <span className="block text-xs text-foreground-400">Który najlepszy?</span>
                </div>
              </Link>
            </div>
          </div>

          {/* Categories section */}
          <div>
            <button
              onClick={() => setMobileCatExpanded(!mobileCatExpanded)}
              className="w-full flex items-center justify-between px-3 py-2 mb-1 cursor-pointer"
            >
              <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                Kategorie produktów
              </h4>
              <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center text-foreground-400 transition-transform duration-200 ${mobileCatExpanded ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileCatExpanded && (
              <div className="grid grid-cols-2 gap-1 px-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-2 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className={`${cat.icon} w-4 h-4 flex items-center justify-center text-foreground-400 flex-shrink-0`}></i>
                    <span className="truncate">{cat.name}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="pt-4 border-t border-background-200/70">
            <p className="text-xs text-foreground-400 px-3">
              &copy; 2026 KakobuyFinds.work
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export { categories };